import { useMutation } from '@apollo/client/react';
import { useState } from 'react';

import { VALIDATION_LIMITS } from '@repo/common';
import {
  type ContactTopic,
  DataTestIds,
  SubmitSupportRequestDocument,
  type SubmitSupportRequestMutation,
} from '@repo/frontend-shared';
import { SDSDialog } from '@repo/sds/SDSDialog';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSText } from '@repo/sds/SDSText';
import { SDSTextArea } from '@repo/sds/SDSTextArea';
import { toast } from '@repo/sds/utils/toast';

interface ContactSupportDialogProps {
  isOpen: boolean;
  /** What the request is about; support sorts requests by it. */
  topic: ContactTopic;
  /** The app page the member is asking from, e.g. `/settings/billing`. */
  pagePath: string;
  /** Starting text the member can edit, so they need not explain the context again. */
  initialMessage?: string;
  onClose(): void;
  /** Called once support has the request. */
  onSent?(): void;
}

function describeSupportError(error: unknown): string {
  return error instanceof Error ? error.message : 'Please try again.';
}

/**
 * Asks StartHub support for help from anywhere in the app.
 *
 * Sends through the same route as the public Get in Touch form, so requests
 * from both land in one place. The member's name and email come from their
 * account, so the only thing to write is the message.
 */
export function ContactSupportDialog({
  isOpen,
  topic,
  pagePath,
  initialMessage = '',
  onClose,
  onSent,
}: ContactSupportDialogProps) {
  const [message, setMessage] = useState(initialMessage);
  // One key for every retry of this dialog, so a double-click is one request.
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  const [submitSupportRequest, { loading: isSending }] = useMutation<SubmitSupportRequestMutation>(
    SubmitSupportRequestDocument,
    {
      onCompleted: () => {
        toast.success('Your message has been sent. Support will reply by email.');
        onSent?.();
        onClose();
      },
      onError: (error: Error) => {
        toast.error(`Your message could not be sent: ${describeSupportError(error)}`);
      },
    },
  );

  const trimmedMessage = message.trim();

  const handleClose = (): void => {
    if (isSending) {
      return;
    }
    onClose();
  };

  return (
    <SDSDialog
      isOpen={isOpen}
      onClose={handleClose}
      title="Contact support"
      size="small"
      dataTestId={DataTestIds.CONTACT_SUPPORT.DIALOG}
      primaryAction={{
        label: 'Send',
        onClick: () => {
          void submitSupportRequest({
            variables: { input: { idempotencyKey, topic, message: trimmedMessage, pagePath } },
          });
        },
        loading: isSending,
        disabled: trimmedMessage.length === 0,
        dataTestId: DataTestIds.CONTACT_SUPPORT.SEND_BTN,
      }}
      secondaryAction={isSending ? undefined : { label: 'Cancel', onClick: handleClose }}
    >
      <SDSFlexbox direction="column" gap="text-large" fullWidth>
        <SDSText
          type="body-large"
          color="text-secondary"
          text="Tell us what you need help with. Support will reply to the email on your account."
        />
        <SDSTextArea
          label="Message"
          value={message}
          onChange={setMessage}
          isDisabled={isSending}
          isFullWidth
          rows={6}
          maxLength={VALIDATION_LIMITS.CONTACT_MESSAGE_MAX}
          dataTestId={DataTestIds.CONTACT_SUPPORT.MESSAGE_INPUT}
        />
      </SDSFlexbox>
    </SDSDialog>
  );
}
